import { AccountCircleOutlined as Account, ExitToApp, Person, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Avatar, Box, Button, ListItemIcon, ListItemText, Menu, Typography } from '@mui/material';
import type React from 'react';
import styled from 'styled-components';
import LinkButton from '../../../common/components/link-button/index.js';
import MenuItemLink from '../../../common/components/menu-item-link/index.js';
import useMenu from '../../../common/hooks/useMenu.js';
import theme from '../../../common/materialTheme/index.js';
import type { UserMenuItemProps, UserMenuProps } from './types.js';

const LoggedOutMenu = styled('div')`
  display: flex;
  & > * {
    margin-right: ${theme.spacing(1)};

    &:last-child {
      margin-right: 0;
    }
  }
`;

const LoggedInMenu = styled(Menu)`
  font-size: 1rem;
`;

const StyledAvatar = styled(Avatar)`
  // Use old height/width from MUI 4
  height: 1.9rem;
  width: 1.9rem;
`;

const StyledButton = styled(Button)`
  color: ${({ theme }) => theme.palette.text.primary};
  padding: 0;
`;

export const UserMenuItem: React.FC<UserMenuItemProps> = (props: UserMenuItemProps): JSX.Element => {
  const { icon: Icon, label, href, ...rest } = props;
  return (
    <MenuItemLink href={href} {...rest}>
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      <ListItemText primary={label} />
    </MenuItemLink>
  );
};

const UserMenu: React.FC<UserMenuProps> = (props: UserMenuProps) => {
  const { user, pathname } = props;
  const { menuRef, openMenu, closeMenu } = useMenu();
  if (!user) {
    const then = `?then=${encodeURIComponent(`${pathname}${window.location.search}`)}`;
    return (
      <LoggedOutMenu id="user-menu">
        <LinkButton variant="contained" title="Sign Up" href={`/signup${then}`}>
          Sign Up
        </LinkButton>
        <LinkButton variant="text" title="Log In" href={`/login${then}`}>
          Log In
        </LinkButton>
      </LoggedOutMenu>
    );
  }

  const userName = user.fullName || user.email;
  return (
    <div id="user-menu">
      <StyledButton
        aria-label="Account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={openMenu}
      >
        <StyledAvatar alt={userName} src={user.avatarPath}>
          <Person htmlColor={theme.palette.text.primary} />
        </StyledAvatar>
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ml: 1
          }}
        >
          <KeyboardArrowDownIcon fontSize="small" sx={{ mt: 1 }} />
        </Box>
      </StyledButton>
      <LoggedInMenu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        id="user-menu-options"
        anchorEl={menuRef}
        keepMounted
        open={Boolean(menuRef)}
        onClose={closeMenu}
        onClick={closeMenu}
      >
        <UserMenuItem icon={Account} label="Account Settings" href="/account-settings" />
        <UserMenuItem icon={VpnKeyIcon} label="Access Tokens" href="/personal-access-tokens" />
        <UserMenuItem icon={ExitToApp} label="Logout" href="/logout" />
      </LoggedInMenu>
    </div>
  );
};

export default UserMenu;
